// A referee's own pay, per tournament: the games they worked, what that comes
// to at their rate, what was paid, and how each payout stands. The sums are
// the organizers' report's own (utils/gameReport), so the two cannot disagree

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Spinner from './Spinner';
import {
  useEntities,
  useMyPayouts,
  useMyRefereeRates,
  useRefereeGames,
} from '../utils/hooks';
import { PayoutStatuses } from '../utils/enums';
import {
  gameRate,
  hasRates,
  moneyStr,
  payDue,
  sumPaid,
  sumPay,
} from '../utils/gameReport';
import './RefereePayouts.scss';

const statusLabels = {
  [PayoutStatuses.PENDING]: 'Sending',
  [PayoutStatuses.PROCESSING]: 'Sending',
  [PayoutStatuses.UNKNOWN]: 'Being checked',
  [PayoutStatuses.SUCCESS]: 'Paid',
  [PayoutStatuses.UNCLAIMED]: 'Unclaimed',
  [PayoutStatuses.ONHOLD]: 'On hold',
  [PayoutStatuses.RETURNED]: 'Returned',
  [PayoutStatuses.REVERSED]: 'Reversed',
  [PayoutStatuses.REFUNDED]: 'Refunded',
};

const noGames = [];

const byStart = (a, b) =>
  `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`);

function TournamentPay({ tournament, games: assigned, payouts }) {
  const { rates, isSynced: isRateSynced } = useMyRefereeRates(tournament.id);

  const games = useMemo(() => [...assigned].sort(byStart), [assigned]);

  const pay = sumPay(games, rates);
  const paid = sumPaid(payouts);
  const due = payDue(pay, paid);

  return (
    <section className='tournament-pay'>
      <Link to={`/tournaments/${tournament.id}`}>
        <h5>{tournament.name}</h5>
      </Link>
      {!isRateSynced ? (
        <Spinner show inline />
      ) : (
        <>
          <p className='rate'>
            {games.length} {games.length === 1 ? 'game' : 'games'} · rate{' '}
            {hasRates(rates) && rates.rate !== undefined
              ? `${moneyStr(rates.rate)} per game`
              : 'not set yet'}
          </p>
          {games.length > 0 && (
            <ul className='games'>
              {games.map((game) => {
                const rate = gameRate(rates, game.id);
                return (
                  <li key={game.id}>
                    <span>
                      {moment(game.date).format('MMM D')}, {game.start}
                    </span>
                    <span>{rate === undefined ? '—' : moneyStr(rate)}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <p className='totals'>
            Earned <span className='font-medium'>{moneyStr(pay)}</span> · Paid{' '}
            <span className='font-medium'>{moneyStr(paid)}</span> · Due{' '}
            <span className='font-medium'>{moneyStr(due)}</span>
          </p>
        </>
      )}
      {payouts.length > 0 && (
        <ul className='payouts'>
          {payouts.map((payout) => (
            <li key={payout.id} className={payout.status}>
              <span>
                {payout.createdAt
                  ? moment(payout.createdAt.toDate()).format('MMM D, YYYY')
                  : '—'}
              </span>
              <span>{moneyStr(payout.amount)}</span>
              <span className='status' title={payout.reason || undefined}>
                {statusLabels[payout.status] ?? 'Failed'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function RefereePayouts({ refereeId }) {
  const { payouts, isSynced: arePayoutsSynced } = useMyPayouts();
  // The games themselves, not just their tournaments: reading each
  // tournament's whole schedule to find them would cost every other game
  const { gamesByTournament, isLoaded: areAssignmentsLoaded } =
    useRefereeGames(refereeId);

  const tournamentIds = useMemo(
    () => [
      ...new Set([
        ...gamesByTournament.keys(),
        ...payouts.map((p) => p.tournamentId),
      ]),
    ],
    [gamesByTournament, payouts],
  );
  const { entities, isResolved } = useEntities('tournaments', tournamentIds);

  const tournaments = useMemo(
    () =>
      entities
        .filter(Boolean)
        .sort((a, b) => b.startDate.valueOf() - a.startDate.valueOf()),
    [entities],
  );

  const payoutsByTournament = useMemo(() => {
    const map = new Map();
    for (const payout of payouts) {
      if (!map.has(payout.tournamentId)) map.set(payout.tournamentId, []);
      map.get(payout.tournamentId).push(payout);
    }
    return map;
  }, [payouts]);

  // "Nothing yet" is a conclusion from absence, so it waits for the server
  if (!arePayoutsSynced || !areAssignmentsLoaded || !isResolved)
    return <Spinner show inline />;

  return (
    <div className='referee-payouts'>
      {tournaments.length === 0 ? (
        <p className='empty'>No games or payouts yet</p>
      ) : (
        <p className='grand-total'>
          Paid in total:{' '}
          <span className='font-medium'>{moneyStr(sumPaid(payouts))}</span>
        </p>
      )}
      {tournaments.map((tournament) => (
        <TournamentPay
          key={tournament.id}
          tournament={tournament}
          games={gamesByTournament.get(tournament.id) ?? noGames}
          payouts={payoutsByTournament.get(tournament.id) ?? []}
        />
      ))}
    </div>
  );
}
